import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/browser';

export function SearchAndFilter({ 
  onSearch, 
  onSortChange, 
  onFilterChange, 
  currentSort = 'newest', 
  currentFilter = 'all',
  currentSearch = '',
  isAuthenticated = false,
  onSemanticSearch,
  hasSemanticResults = false
}) {
  const [searchTerm, setSearchTerm] = useState(currentSearch);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isSemanticSearching, setIsSemanticSearching] = useState(false);
  const [semanticSearchError, setSemanticSearchError] = useState(null);

  // Update search term when currentSearch changes (for external updates)
  useEffect(() => {
    setSearchTerm(currentSearch);
  }, [currentSearch]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Use semantic search when form is submitted (Enter key)
    handleSemanticSearch();
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // If the search term is cleared, clear semantic search results
    if (value === '' && currentSearch !== '') {
      console.log('SearchAndFilter: Clearing search');
      if (onSearch) {
        onSearch('');
      }
    }
  };

  const handleSortChange = (e) => {
    const newSort = e.target.value;
    console.log('SearchAndFilter: Changing sort to:', newSort);
    
    // Track sort change
    import('/src/services/analytics/index.js').then(({ trackChangeSort }) => {
      trackChangeSort(newSort);
    });
    
    if (onSortChange) {
      onSortChange(newSort);
    }
  };

  const handleFilterChange = (newFilter) => {
    console.log('SearchAndFilter: Changing filter to:', newFilter);
    
    // Track filter change
    import('/src/services/analytics/index.js').then(({ trackChangeFilter }) => {
      trackChangeFilter(newFilter);
    });
    
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const clearSearch = () => {
    console.log('SearchAndFilter: Clearing search via button');
    setSearchTerm('');
    setSemanticSearchError(null);
    if (onSearch) {
      onSearch('');
    }
  };

  const handleSemanticSearch = async () => {
    if (!searchTerm.trim() || isSemanticSearching) return;
    
    setIsSemanticSearching(true);
    setSemanticSearchError(null);

    try {
      console.log('SearchAndFilter: Starting semantic search for:', searchTerm);
      
      const response = await fetch('/api/check-similar-requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: searchTerm,
          description: searchTerm // Use search term as both title and description for semantic matching
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Smart Search failed');
      }

      const data = await response.json();
      console.log('SearchAndFilter: Semantic search completed:', data);
      
      // Track the semantic search
      const { trackSearchRequests } = await import('/src/services/analytics/index.js');
      trackSearchRequests(searchTerm, 'semantic', data.similar_requests?.length || 0);
      
      if (onSemanticSearch) {
        onSemanticSearch(data, searchTerm);
      }
      
    } catch (error) {
      console.error('Error in semantic search:', error);
      Sentry.captureException(error, {
        extra: { 
          searchTerm,
          errorMessage: error.message 
        }
      });
      
      setSemanticSearchError('Smart Search temporarily unavailable. Please try again later.');
    } finally {
      setIsSemanticSearching(false);
    }
  };

  const filterOptions = [
    { value: 'all', label: 'All Requests', icon: '🌟' },
    ...(isAuthenticated ? [
      { value: 'my-requests', label: 'My Requests', icon: '✏️' },
      { value: 'liked', label: 'My Votes', icon: '❤️' }
    ] : [])
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First', icon: '🆕' },
    { value: 'oldest', label: 'Oldest First', icon: '📅' },
    { value: 'most_committed', label: 'Most Votes', icon: '🔥' },
    { value: 'trending', label: 'Trending', icon: '📈' }
  ];

  return (
    <div className="space-y-6">
      {/* Search Section */}
      <div className="relative">
        <form onSubmit={handleSearchSubmit}>
          <div className={`relative transition-all duration-300 ${isSearchFocused ? 'transform scale-[1.02]' : ''}`}>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              placeholder="Describe what kind of app you're looking for..."
              className={`w-full pl-12 sm:pl-16 pr-12 sm:pr-16 py-4 sm:py-6 text-base sm:text-xl border-2 rounded-2xl transition-all duration-300 bg-white/80 backdrop-blur-sm placeholder-gray-500 
                ${isSearchFocused 
                  ? 'border-indigo-500 ring-4 ring-indigo-500/20 bg-white shadow-2xl' 
                  : 'border-gray-200 hover:border-indigo-300 shadow-lg hover:shadow-xl'
                } box-border`}
            />
            <div className="absolute inset-y-0 left-0 pl-4 sm:pl-6 flex items-center pointer-events-none">
              <svg className={`h-5 w-5 sm:h-7 sm:w-7 transition-colors duration-300 ${isSearchFocused ? 'text-indigo-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {searchTerm && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-4 sm:pr-6 flex items-center cursor-pointer text-gray-400 hover:text-gray-600 transition-colors duration-200 hover:scale-110"
              >
                <svg className="h-5 w-5 sm:h-7 sm:w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <button type="submit" className="sr-only">Search</button>
        </form>

        {/* Search Button - Mobile Optimized */}
        <div className="mt-4">
          <button
            type="button"
            onClick={handleSemanticSearch}
            disabled={!searchTerm.trim() || isSemanticSearching}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center text-base sm:text-lg"
          >
            {isSemanticSearching ? (
              <>
                <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-white border-t-transparent rounded-full animate-spin mr-2 sm:mr-3"></div>
                <span className="text-sm sm:text-base">Analyzing...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="text-sm sm:text-base">Search Requests</span>
              </>
            )}
          </button>
        </div>

        {/* Search explanation - Mobile Optimized */}
        <div className="mt-3 text-center">
          <p className="text-xs sm:text-sm text-gray-600">
            🤖 <strong>AI-powered search</strong> finds similar requests
          </p>
        </div>

        {/* Semantic Search Error - Mobile Optimized */}
        {semanticSearchError && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-2 sm:p-3">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-red-500 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-xs sm:text-sm text-red-800">{semanticSearchError}</p>
            </div>
          </div>
        )}
      </div>

      {/* Filter and Sort Buttons - Hidden when showing semantic search results */}
      {!hasSemanticResults && (
        <div className="flex flex-col space-y-4">
          {/* Filter Buttons - Only show if authenticated (multiple options available) */}
          {isAuthenticated && (
            <div>
              <h3 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3 uppercase tracking-wide">Filter by</h3>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {filterOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleFilterChange(option.value)}
                    className={`group px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl font-semibold text-xs sm:text-sm transition-all duration-300 transform hover:scale-105 border-2 ${
                      currentFilter === option.value
                        ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white border-transparent shadow-xl'
                        : 'bg-white/80 backdrop-blur-sm text-gray-700 hover:text-indigo-600 border-gray-200 hover:border-indigo-300 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <span className="text-sm sm:text-base">{option.icon}</span>
                      <span>{option.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sort Dropdown */}
          <div>
            <h3 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3 uppercase tracking-wide">Sort by</h3>
            <select
              value={currentSort}
              onChange={handleSortChange}
              className="w-full px-4 sm:px-6 py-2 sm:py-3 bg-white/80 backdrop-blur-sm border-2 border-gray-200 rounded-xl sm:rounded-2xl text-sm sm:text-base font-medium text-gray-700 hover:border-indigo-300 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.icon} {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}